import {
  fieldWorkSources,
  fieldWorkStatuses,
  isOpenStatus,
  type FieldWorkRequest,
  type FieldWorkSource,
  type FieldWorkStatus,
} from './fieldWorkRequest';
import { fieldOpsTeams, type FieldOpsTeam } from './fieldOpsTeam';
import type { FieldWorkStatsWire } from './validation';

/**
 * Client-side aggregation for the Field Work reporting view (issue #7957
 * success criteria: backlog, workload, completion time, time blocked,
 * source mix). Computed from the full request list the queue already
 * fetches -- a server-side `GetFieldWorkStats` RPC is the follow-up once
 * volume makes list-and-compute impractical (OPEN_QUESTIONS.md P2).
 */
export interface CompletionTimeStats {
  /** Requests with both requestedAt and completedAt. */
  count: number;
  /** completedAt - requestedAt (request raised -> done). */
  leadHoursAvg: number;
  leadHoursMedian: number;
  /** completedAt - startedAt, for the subset that recorded a start. */
  cycleCount: number;
  cycleHoursAvg: number;
  cycleHoursMedian: number;
}

export interface BlockedTimeStats {
  /** Ever spent time blocked (accumulated seconds > 0 or blocked right now). */
  count: number;
  currentlyBlocked: number;
  totalHours: number;
  avgHoursPerRequest: number;
}

export interface AssigneeLoad {
  /** '' means unassigned. */
  assignee: string;
  open: number;
  total: number;
}

export interface FieldWorkStats {
  total: number;
  open: number;
  closed: number;
  overdue: number;
  unassignedOpen: number;
  byStatus: Record<FieldWorkStatus, number>;
  byTeam: Record<FieldOpsTeam, { open: number; total: number }>;
  bySource: { source: FieldWorkSource | 'UNSPECIFIED'; open: number; total: number }[];
  byAssignee: AssigneeLoad[];
  completion: CompletionTimeStats;
  blocked: BlockedTimeStats;
}

const HOUR_MS = 3600 * 1000;

function hoursBetween(fromISO: string | undefined, toISO: string | undefined): number | undefined {
  if (!fromISO || !toISO) return undefined;
  const from = Date.parse(fromISO);
  const to = Date.parse(toISO);
  if (Number.isNaN(from) || Number.isNaN(to) || to < from) return undefined;
  return (to - from) / HOUR_MS;
}

function mean(values: number[]): number {
  if (values.length === 0) return 0;
  return values.reduce((sum, v) => sum + v, 0) / values.length;
}

function median(values: number[]): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  if (sorted.length % 2 === 0) return ((sorted[mid - 1] ?? 0) + (sorted[mid] ?? 0)) / 2;
  return sorted[mid] ?? 0;
}

export function computeFieldWorkStats(
  requests: readonly FieldWorkRequest[],
  now: number = Date.now(),
): FieldWorkStats {
  const byStatus = Object.fromEntries(
    fieldWorkStatuses.map((status) => [status, 0]),
  ) as Record<FieldWorkStatus, number>;
  const byTeam = Object.fromEntries(
    fieldOpsTeams.map((team) => [team, { open: 0, total: 0 }]),
  ) as Record<FieldOpsTeam, { open: number; total: number }>;
  const sourceKeys = [...fieldWorkSources, 'UNSPECIFIED'] as const;
  const bySourceMap = new Map<(typeof sourceKeys)[number], { open: number; total: number }>(
    sourceKeys.map((key) => [key, { open: 0, total: 0 }]),
  );
  const assigneeMap = new Map<string, AssigneeLoad>();

  let open = 0;
  let overdue = 0;
  let unassignedOpen = 0;

  const leadHours: number[] = [];
  const cycleHours: number[] = [];

  let blockedCount = 0;
  let currentlyBlocked = 0;
  let blockedSeconds = 0;

  for (const request of requests) {
    const isOpen = isOpenStatus(request.status);
    byStatus[request.status] += 1;

    const team = byTeam[request.team];
    if (team) {
      team.total += 1;
      if (isOpen) team.open += 1;
    }

    const sourceKey = request.source ?? 'UNSPECIFIED';
    const sourceBucket = bySourceMap.get(sourceKey);
    if (sourceBucket) {
      sourceBucket.total += 1;
      if (isOpen) sourceBucket.open += 1;
    }

    const assigneeLoad = assigneeMap.get(request.assignee) ?? {
      assignee: request.assignee,
      open: 0,
      total: 0,
    };
    assigneeLoad.total += 1;
    if (isOpen) assigneeLoad.open += 1;
    assigneeMap.set(request.assignee, assigneeLoad);

    if (isOpen) {
      open += 1;
      if (!request.assignee) unassignedOpen += 1;
      if (request.dueDate && Date.parse(request.dueDate) < now) overdue += 1;
    }

    const lead = hoursBetween(request.requestedAt, request.completedAt);
    if (lead !== undefined) leadHours.push(lead);
    const cycle = hoursBetween(request.startedAt, request.completedAt);
    if (cycle !== undefined) cycleHours.push(cycle);

    let requestBlockedSeconds = request.blockedTotalSeconds;
    if (request.status === 'BLOCKED' && request.blockedSince) {
      const since = Date.parse(request.blockedSince);
      if (!Number.isNaN(since) && since <= now) {
        requestBlockedSeconds += Math.floor((now - since) / 1000);
      }
      currentlyBlocked += 1;
    }
    if (requestBlockedSeconds > 0) {
      blockedCount += 1;
      blockedSeconds += requestBlockedSeconds;
    }
  }

  return {
    total: requests.length,
    open,
    closed: requests.length - open,
    overdue,
    unassignedOpen,
    byStatus,
    byTeam,
    bySource: sourceKeys
      .map((source) => ({ source, ...bySourceMap.get(source)! }))
      .filter((row) => row.total > 0)
      .sort((a, b) => b.total - a.total),
    byAssignee: [...assigneeMap.values()].sort(
      (a, b) => b.open - a.open || b.total - a.total || a.assignee.localeCompare(b.assignee),
    ),
    completion: {
      count: leadHours.length,
      leadHoursAvg: mean(leadHours),
      leadHoursMedian: median(leadHours),
      cycleCount: cycleHours.length,
      cycleHoursAvg: mean(cycleHours),
      cycleHoursMedian: median(cycleHours),
    },
    blocked: {
      count: blockedCount,
      currentlyBlocked,
      totalHours: blockedSeconds / 3600,
      avgHoursPerRequest: blockedCount === 0 ? 0 : blockedSeconds / 3600 / blockedCount,
    },
  };
}

/**
 * Map the wire shape of `GetFieldWorkStats` onto the same `FieldWorkStats`
 * the browser-side `computeFieldWorkStats` produces, so the reports view
 * doesn't care which one it got.
 */
export function fieldWorkStatsFromWire(wire: FieldWorkStatsWire): FieldWorkStats {
  const byStatus = Object.fromEntries(fieldWorkStatuses.map((s) => [s, 0])) as Record<
    FieldWorkStatus,
    number
  >;
  for (const row of wire.byStatus) {
    if ((fieldWorkStatuses as readonly string[]).includes(row.status)) {
      byStatus[row.status as FieldWorkStatus] = row.count;
    }
  }

  const byTeam = Object.fromEntries(
    fieldOpsTeams.map((t) => [t, { open: 0, total: 0 }]),
  ) as Record<FieldOpsTeam, { open: number; total: number }>;
  for (const row of wire.byTeam) {
    if ((fieldOpsTeams as readonly string[]).includes(row.team)) {
      byTeam[row.team as FieldOpsTeam] = { open: row.open, total: row.total };
    }
  }

  const knownSources = new Set<string>(fieldWorkSources);
  const bySource = wire.bySource
    .filter((row) => row.total > 0)
    .map((row) => ({
      source: (knownSources.has(row.source) ? row.source : 'UNSPECIFIED') as
        | FieldWorkSource
        | 'UNSPECIFIED',
      open: row.open,
      total: row.total,
    }));

  return {
    total: wire.total,
    open: wire.open,
    closed: wire.closed,
    overdue: wire.overdue,
    unassignedOpen: wire.unassignedOpen,
    byStatus,
    byTeam,
    bySource,
    byAssignee: wire.byAssignee.map((row) => ({
      assignee: row.assignee,
      open: row.open,
      total: row.total,
    })),
    completion: {
      count: wire.leadTime.count,
      leadHoursAvg: wire.leadTime.avgHours,
      leadHoursMedian: wire.leadTime.medianHours,
      cycleCount: wire.cycleTime.count,
      cycleHoursAvg: wire.cycleTime.avgHours,
      cycleHoursMedian: wire.cycleTime.medianHours,
    },
    blocked: {
      count: wire.blocked.count,
      currentlyBlocked: wire.currentlyBlocked,
      totalHours: wire.blockedTotalHours,
      avgHoursPerRequest: wire.blocked.avgHours,
    },
  };
}

/** "3d 4h", "6h", "45m" — compact duration from a fractional hour count. */
export function formatHours(hours: number): string {
  if (!Number.isFinite(hours) || hours <= 0) return '—';
  const totalMinutes = Math.round(hours * 60);
  const days = Math.floor(totalMinutes / (60 * 24));
  const rem = totalMinutes - days * 60 * 24;
  const h = Math.floor(rem / 60);
  const m = rem - h * 60;
  if (days > 0) return `${days}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}
